import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { OrgOut, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { useOrg } from "../lib/data.ts";
import { orgApi } from "../lib/paths.ts";
import { bump, useAction } from "../lib/resource.ts";
import { atLeast } from "../lib/roles.ts";
import { loadMe, type PageProps, reloadAndGoHome } from "../lib/state.ts";
import { toast } from "../lib/toast.ts";
import { OrgLogo } from "../ui/Avatar.tsx";
import { Button } from "../ui/Button.tsx";
import { ConfirmButton } from "../ui/Dialog.tsx";
import { Checkbox, Field, Form, Input, Textarea } from "../ui/form.tsx";
import { Async, Badge, Section } from "../ui/misc.tsx";
import { PictureField } from "../ui/PictureEditor.tsx";
import { OthersPrivateToggle } from "./items/othersPrivate.tsx";
import { SettingsNav } from "./settings/nav.tsx";
import { useSaveSettings } from "./settings/save.ts";

interface Props {
  space: SpaceOut;
  org: OrgOut;
  admin: boolean;
}

/** A textarea's lines, trimmed, the empty ones left out. */
const lines = (text: string): string[] =>
  text
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * Allowed senders whose mail is accepted though it isn't verified (an admin chose to, allowing them): each marked, with
 * a way to require verification again.
 */
function Unverified(p: Props) {
  const [patch, busy] = useSaveSettings(orgApi(p.space));
  const loose = p.org.allowlist.filter((e) => e.verify === false);
  return loose.length === 0 ? null : (
    <div class="flex flex-col gap-2">
      <p class="text-sm text-zinc-600">{t("settings.unverifiedHint")}</p>
      <ul class="flex flex-col gap-2">
        {loose.map((e) => (
          <li key={e.sender} class="flex flex-wrap items-center gap-2 text-sm">
            <span class="break-all text-zinc-800">{e.sender}</span>
            <Badge tone="amber">{t("held.unverified")}</Badge>
            {p.admin ? (
              <Button
                size="xs"
                busy={busy}
                onClick={() =>
                  patch({
                    allowlist: p.org.allowlist.map((x) =>
                      x.sender === e.sender ? { sender: x.sender, verify: true } : x,
                    ),
                  })
                }
              >
                {t("settings.requireVerification")}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The organization's logo: admins upload (cropped first) and remove it, each on its own. */
function Logo({ space, org, admin }: Props) {
  const saved = async (message: string) => {
    toast(message);
    bump(); // this page's org
    await loadMe(); // the sidebar's and the organizations' list's
  };
  const path = orgApi(space, "/logo");
  return (
    <PictureField
      label={t("settings.logo")}
      hint={t("settings.logo.hint")}
      round={false}
      has={Boolean(org.logo)}
      preview={<OrgLogo id={org.id} name={org.name} logo={org.logo ?? null} size={64} />}
      {...(admin && {
        onUpload: async (picture: Blob) => {
          await api.putFile<OrgOut>(path, picture);
          await saved(t("settings.logo.saved"));
        },
        onRemove: async () => {
          await api.del<OrgOut>(path);
          await saved(t("settings.logo.removed"));
        },
      })}
    />
  );
}

function General(p: Props) {
  const [name, setName] = useState(p.org.name);
  const [allow, setAllow] = useState(p.org.allowlist.map((e) => e.sender).join("\n"));
  const [block, setBlock] = useState(p.org.blocklist.join("\n"));
  const [patch, busy] = useSaveSettings(orgApi(p.space));
  return (
    <Section title={t("settings.general")}>
      <div class="mb-4">
        <Logo {...p} />
      </div>
      <Form
        class="flex max-w-xl flex-col gap-4"
        onSubmit={() => patch({ name, allowlist: lines(allow), blocklist: lines(block) })}
      >
        <Field label={t("settings.name")}>
          <Input value={name} onValue={setName} required={true} disabled={!p.admin} />
        </Field>
        <Field label={t("settings.allowlist")} hint={t("settings.allowlistHint")} anchor="allowed-senders">
          <Textarea value={allow} onValue={setAllow} disabled={!p.admin} />
        </Field>
        <Unverified {...p} />
        <Field label={t("settings.blocklist")} hint={t("settings.blocklistHint")} anchor="blocked-senders">
          <Textarea value={block} onValue={setBlock} disabled={!p.admin} />
        </Field>
        {p.admin ? (
          <Button class="self-start" submit={true} busy={busy}>
            {t("common.save")}
          </Button>
        ) : null}
      </Form>
    </Section>
  );
}

/** Whether you see other members' private items here (admins and owners; just for you). */
function OthersPrivate({ space }: Props) {
  return (
    <Section title={t("othersPrivate.section")} anchor="private-items">
      <p class="mb-3 max-w-xl text-sm text-zinc-600">{t("othersPrivate.hint")}</p>
      <OthersPrivateToggle space={space} />
    </Section>
  );
}

/**
 * Whether editors set up agents (on unless turned off): admins switch it; everyone sees how it is. Off, agents editors
 * set up already keep working, and they still manage them.
 */
function EditorsAddAgents({ space, org, admin }: Props) {
  const [patch, busy] = useSaveSettings(orgApi(space));
  return (
    <Section title={t("settings.agents")} anchor="editors-agents">
      <div class="flex max-w-xl flex-col gap-1">
        <Checkbox
          checked={org.editors_add_agents}
          disabled={!admin || busy}
          onCheck={(on) => patch({ editors_add_agents: on })}
          label={t("settings.editorsAddAgents")}
        />
        <p class="text-sm text-zinc-600">{t("settings.editorsAddAgentsHint")}</p>
      </div>
    </Section>
  );
}

/** Deleting the organization (owners, typing its name to confirm): every space goes. */
function Danger({ space, org }: Props) {
  const [remove, removing] = useAction(async () => {
    await api.del(orgApi(space));
    await reloadAndGoHome();
  });
  return (
    <Section title={t("settings.data")}>
      <ConfirmButton
        variant="danger"
        label={t("settings.deleteOrg")}
        title={t("settings.deleteOrg")}
        message={t("settings.deleteOrgMessage")}
        confirmLabel={t("settings.deleteOrg")}
        typeToConfirm={org.name}
        busy={removing}
        onConfirm={remove}
      />
    </Section>
  );
}

/** The organization: name and allowed senders, (admins) others' private items, and deletion (its spaces have their own tab). */
export function OrgSettings({ space }: PageProps) {
  const org = useOrg(space);
  const admin = atLeast(space.role, "admin");
  return (
    <>
      <SettingsNav space={space} tab="/org" />
      <Async r={org}>
        {(o) => {
          const p: Props = { space, org: o, admin };
          return (
            <>
              <General {...p} />
              <EditorsAddAgents {...p} />
              {admin ? <OthersPrivate {...p} /> : null}
              {o.role === "owner" ? <Danger {...p} /> : null}
            </>
          );
        }}
      </Async>
    </>
  );
}
